import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8085';

const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ── 401 / token-refresh interceptor ─────────────────────────────────────────
//
// Flow on 401:
//   1. Call POST /auth/web/refresh (httpOnly cookie carries the refresh token)
//   2. Retry the original request — browser now sends the new access-token cookie
//   3. Concurrent 401s that arrive while refresh is in-flight are queued and
//      replayed once the refresh resolves.
//
// On double 401 (refresh call itself fails / refresh token expired):
//   Dispatch LOGOUT_USER so Redux clears auth state → app redirects to sign-in.
//
// setupInterceptors(store) must be called once from App.tsx to wire up the store.
// Using a lazy reference avoids the circular-dep chain:
//   config → store → sagas → authSaga → authNode → config
// ────────────────────────────────────────────────────────────────────────────

type Dispatcher = { dispatch: (action: { type: string }) => void };

let _store: Dispatcher | null = null;
let isRefreshing = false;
let pendingQueue: Array<{ resolve: () => void; reject: (err: any) => void }> = [];

const drainQueue = (err: any) => {
  pendingQueue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve()));
  pendingQueue = [];
};

/** Wire up the Redux store reference so the interceptor can dispatch logout. */
export const setupInterceptors = (store: Dispatcher): void => {
  _store = store;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const url: string = config?.url ?? '';

    // Never intercept refresh / logout endpoints — prevents infinite loops
    if (url.includes('/auth/web/refresh') || url.includes('/auth/web/logout')) {
      return Promise.reject(error);
    }

    // Only handle 401; anything else (403, 500 …) propagates normally
    if (error.response?.status !== 401 || config._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Another refresh is already in-flight — queue this request for replay
      return new Promise<void>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      })
        .then(() => api(config))
        .catch((err) => Promise.reject(err));
    }

    config._retry = true;
    isRefreshing = true;

    try {
      // Use raw axios (not `api`) so this call bypasses the interceptor above
      await axios.post(`${BASE_URL}/auth/web/refresh`, {}, { withCredentials: true });

      drainQueue(null);
      return api(config); // replay the original request with fresh cookie
    } catch (refreshError) {
      drainQueue(refreshError);
      // Refresh token expired — force the user to sign in again
      _store?.dispatch({ type: '@@auth/login/LOGOUT_USER' });
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
